import { fetchAuthSession } from 'aws-amplify/auth';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { logger } from '@/lib/logger';
import { RootState } from '@/redux/store';
import { HipaaConsentModal } from './HipaaConsentModal';

interface HipaaConsentCheckProps {
  children: React.ReactNode;
  selectedClient: any;
}

export const HipaaConsentCheck = ({ children, selectedClient }: HipaaConsentCheckProps) => {
  const [showConsent, setShowConsent] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const userRole = useSelector<RootState, string | null>((state) => state.user.role);

  // Check if HIPAA consent is needed
  useEffect(() => {
    const checkHipaaConsent = async () => {
      // Only check for CLIENT role
      if (userRole !== 'CLIENT') return;

      // Check if client data is available and doesn't have consent
      if (
        selectedClient &&
        selectedClient.role === 'CLIENT' &&
        selectedClient.hipaa_consent === false
      ) {
        try {
          const sessionData = await fetchAuthSession();
          if (sessionData?.tokens?.accessToken) {
            setAccessToken(sessionData.tokens.accessToken.toString());
            setShowConsent(true);
          }
        } catch (error) {
          logger.error('Error fetching auth session:', error);
        }
      }
    };

    checkHipaaConsent();
  }, [selectedClient, userRole]);

  const handleConsentComplete = () => {
    setShowConsent(false);
    // Update client data in state
    if (selectedClient) {
      selectedClient.hipaa_consent = true;
      selectedClient.hipaa_consent_date = new Date().toISOString();
      selectedClient.hipaa_consent_version = '1.0';
    }
  };

  return (
    <div className="h-full min-h-0" data-testid="hipaaConsent_root">
      {showConsent && selectedClient && accessToken && (
        <HipaaConsentModal
          clientName={`${selectedClient.first_name} ${selectedClient.last_name}`}
          clientId={selectedClient.id}
          accessToken={accessToken}
          onConsentComplete={handleConsentComplete}
        />
      )}
      <div className="h-full min-h-0" data-testid="hipaaConsent_children">
        {children}
      </div>
    </div>
  );
};

export default HipaaConsentCheck;
